// "View Paceroo as {first}" — start a support visit inside the runner's
// grant: reason, read-only or (if the runner allowed it) changes, and a
// length capped at the grant's expiry. The one-time link opens in a new tab
// on the runner app's host. Rules: apps/api/src/support/service.ts.

import { ReasonHint } from "./ReasonHint";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { MIN_REASON_LENGTH, type AdminRunnerDetail } from "@paceroo/domain/views/admin";
import { SUPPORT_SESSION_MINUTES, type ImpersonationStart, type SupportScope, type SupportSessionMinutes } from "@paceroo/domain/views/support";
import { AdminApiError, adminPost } from "../lib/api";
import { fD, fT, firstName, parseWhen } from "../lib/format";
import { useShell } from "../shell/context";
import a from "./ActionDialog.module.css";
import s from "./ImpersonateDialog.module.css";

export function ImpersonateDialog({
  detail,
  onCancel,
  onStarted,
}: {
  detail: AdminRunnerDetail;
  onCancel: () => void;
  onStarted: (result: ImpersonationStart, minutes: number, mode: SupportScope) => void;
}) {
  const { me, bp } = useShell();
  const r = detail.runner;
  const grant = detail.supportAccess.active!;
  const first = firstName(r.name);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState<SupportScope>("read");
  const [minutes, setMinutes] = useState<SupportSessionMinutes>(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const prod = me.environment === "prod";
  const now = Date.now();
  const grantEnds = parseWhen(grant.expiresAt).getTime();
  const grantMinutesLeft = Math.max(0, Math.floor((grantEnds - now) / 60_000));
  const effective = Math.min(minutes, grantMinutesLeft);
  const writeAllowed = grant.scope === "write";

  useEffect(() => {
    reasonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const blocked = busy || reason.trim().length < MIN_REASON_LENGTH || effective < 1;
  const start = new Date(now);
  const end = new Date(now + effective * 60_000);

  const submit = async () => {
    if (blocked) return;
    // Open the tab inside the click so the browser allows it; point it at
    // the handoff once the API has issued one.
    const tab = window.open("about:blank", "_blank");
    setBusy(true);
    setError(null);
    try {
      const result = await adminPost<ImpersonationStart>(`runners/${encodeURIComponent(r.id)}/impersonation`, { reason: reason.trim(), mode, minutes });
      if (tab) {
        tab.opener = null;
        tab.location.href = result.handoffUrl;
      } else {
        window.location.assign(result.handoffUrl);
      }
      onStarted(result, effective, mode);
    } catch (err) {
      tab?.close();
      setError(err instanceof AdminApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  const modes: Array<[SupportScope, string, string]> = [
    ["read", "Read-only", `See exactly what ${first} sees. Saving, exporting and assistant changes are blocked.`],
    ["write", "Allow changes", `Edits save to ${first}’s account and appear in their history as made by Paceroo support.`],
  ];

  return (
    <div className={clsx(a.scrim, bp === "mobile" && a.scrimSheet)} onClick={() => !busy && onCancel()}>
      <div role="dialog" aria-modal="true" aria-labelledby="imp-title" className={clsx(a.dialog, s.dialog, bp === "mobile" && a.sheet)} onClick={(e) => e.stopPropagation()}>
        <div className={clsx(a.env, prod ? a.envProd : a.envDev)}>
          <span className={a.envBullet} />
          {prod ? "Production · you will see a real runner’s data" : `${me.environment === "local" ? "Local" : "Dev"} · ${window.location.host} · test data only`}
        </div>
        <div className={clsx(a.body, s.body)}>
          <span className="kicker ink-mute">Sign in as runner · with permission</span>
          <h2 id="imp-title" className={clsx("t-serif-md", a.title)}>
            View Paceroo as {first}
          </h2>
          <div className={s.grant}>
            <span className={s.goldDot} />
            <span className="t-body-sm">
              Support access granted {fD(parseWhen(grant.grantedAt))}, {fT(parseWhen(grant.grantedAt))} · {writeAllowed ? "changes allowed" : "read-only"}
            </span>
            <span className={clsx("t-mono-xs ink-mute", s.left)}>{grantMinutesLeft >= 120 ? `${Math.round(grantMinutesLeft / 60)} h left` : `${grantMinutesLeft} min left`}</span>
          </div>
          <label className={a.field}>
            <span className="t-label">
              Reason <span className={clsx("ink-mute", a.hint)}>· required, shown in the audit log</span>
            </span>
            <textarea
              ref={reasonRef}
              className={clsx(a.control, a.textarea, s.reason)}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What you’re trying to reproduce"
            />
            <ReasonHint reason={reason} />
          </label>
          <div className={a.field}>
            <span className="t-label">Mode</span>
            {modes.map(([key, label, desc]) => {
              const disabled = key === "write" && !writeAllowed;
              return (
                <button
                  key={key}
                  type="button"
                  className={clsx(s.mode, mode === key && s.modeOn, mode === key && key === "write" && s.modeWrite)}
                  aria-pressed={mode === key}
                  disabled={disabled}
                  title={disabled ? `${first} allowed read-only access` : undefined}
                  onClick={() => setMode(key)}
                >
                  <span className={clsx(s.radio, mode === key && s.radioOn)}>
                    <span className={s.radioDot} />
                  </span>
                  <span className={s.modeText}>
                    <span className={clsx("t-label", s.modeLabel)}>{label}</span>
                    <span className="t-body-sm ink-mute">{disabled ? `${first} allowed read-only access.` : desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className={a.field}>
            <span className="t-label">Length</span>
            <div className={s.chips}>
              {SUPPORT_SESSION_MINUTES.map((n) => (
                <button key={n} type="button" className="chip" data-on={minutes === n ? "true" : undefined} disabled={n > grantMinutesLeft && n !== SUPPORT_SESSION_MINUTES[0]} onClick={() => setMinutes(n)}>
                  {n} min
                </button>
              ))}
            </div>
            {effective < minutes && <span className="t-mono-xs ink-mute">Capped at the grant’s expiry: {effective} min.</span>}
          </div>
          <div className={s.preview}>
            <span className="kicker ink-mute">{first} will see in Profile</span>
            <span className={clsx("t-body-sm", s.previewText)}>
              “Paceroo support viewed your account on {fD(start)}, {fT(start)}–{fT(end)}
              {mode === "read" ? ", read-only." : " and could make changes."}”
            </span>
          </div>
          {error && <p className={clsx("t-body-sm", a.error)}>{error}</p>}
        </div>
        <div className={clsx(a.buttons, bp === "mobile" && a.buttonsStacked)}>
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => void submit()} disabled={blocked}>
            {busy ? "Starting…" : `Start ${effective}-min session →`}
          </button>
        </div>
      </div>
    </div>
  );
}
